// Client layer (TS-2.5.03) - the owner's "Notes" section on the roadmap: what has changed lately,
// in sentences.
//
// THIS COMPONENT PRINTS; IT DOES NOT COMPOSE. Every line arrives from the server as a finished
// sentence (ChangeFeedItem.sentence), because the narrator that builds it lives beside the audit
// store and this app is never handed the parts - no action token, no before/after payload, no
// principal id. That is not a convenience: the feed is served on roadmap.view while the audit
// trail proper is served on audit.view, and the reason the wider gate is defensible is precisely
// that the object crossing the wire has already had everything else removed. A renderer that
// assembled lines from fields would be a renderer that could assemble one that should not exist.
//
// THE DEFAULT IS A SHORT LIST WITH A CONTROL, not a scrollbox. The card sits below the detail
// sections on a page that is already long, so it shows the six most recent lines and offers the
// rest behind one button - which keeps the page's print layout sane and means a reader who wants
// the summary never has to scroll a nested region to get past it.
//
// TIMES ARE FORMATTED IN THE READER'S LOCALE from a UTC instant, the same conversion the roadmap
// header does for dataAsOfUtc. The <time> element carries the raw ISO value so the machine-readable
// instant is not lost to the formatting.
import { useState } from "react";
import { useRecentChanges } from "./hooks/useRecentChanges";
import type { ChangeFeedItem } from "../../api/generated/contracts";
import { strings } from "../../strings";

/** How far back the card asks about. The server clamps, so this is a request and not a limit. */
export const RECENT_CHANGES_WINDOW_DAYS = 30;

/** How many lines the card asks the server for. */
export const RECENT_CHANGES_FETCH_COUNT = 50;

/** How many of them it shows before the reader asks for the rest. */
export const RECENT_CHANGES_COLLAPSED_COUNT = 6;

function formatWhen(occurredAtUtc: string): string {
  const at = new Date(occurredAtUtc);
  if (Number.isNaN(at.getTime())) return occurredAtUtc;
  return at.toLocaleString();
}

function ChangeLine({ item }: { readonly item: ChangeFeedItem }) {
  return (
    <li className="eg-recent-changes__item">
      <p className="eg-recent-changes__sentence">{item.sentence}</p>
      <p className="eg-recent-changes__meta">
        <time dateTime={item.occurredAtUtc}>{formatWhen(item.occurredAtUtc)}</time>
        {" · "}
        {item.actorDisplayName}
      </p>
    </li>
  );
}

export function RecentChanges() {
  const [expanded, setExpanded] = useState(false);
  const { feed, loading, error } = useRecentChanges(RECENT_CHANGES_WINDOW_DAYS, RECENT_CHANGES_FETCH_COUNT);

  const copy = strings.roadmap.recentChanges;

  // The frame is rendered in every state, including the failed one, so the section does not appear
  // and disappear under a reader as the fetch settles - a heading that comes and goes is a layout
  // shift on a page somebody is reading.
  return (
    <section className="eg-recent-changes" aria-labelledby="eg-recent-changes-title">
      <div className="eg-recent-changes__header">
        <h2 id="eg-recent-changes-title">{copy.title}</h2>
        <p className="eg-recent-changes__window">{copy.windowLabel}</p>
      </div>

      {loading && !feed && (
        <div role="status" aria-live="polite" className="eg-recent-changes__note">
          {copy.loading}
        </div>
      )}

      {error && (
        <p role="status" className="eg-recent-changes__note">
          {copy.error}
        </p>
      )}

      {!error && feed && feed.items.length === 0 && <p className="eg-recent-changes__note">{copy.empty}</p>}

      {!error && feed && feed.items.length > 0 && (
        <>
          <ul className="eg-recent-changes__list" aria-label={copy.aria.list}>
            {(expanded ? feed.items : feed.items.slice(0, RECENT_CHANGES_COLLAPSED_COUNT)).map((item, index) => (
              // The index is part of the key because one save legitimately yields several lines -
              // a status move and a go-live move share an instant and an actor - so neither the
              // timestamp nor the sentence alone is unique across the list.
              <ChangeLine key={`${item.occurredAtUtc}-${index}`} item={item} />
            ))}
          </ul>

          {feed.items.length > RECENT_CHANGES_COLLAPSED_COUNT && (
            <button type="button" className="eg-recent-changes__toggle" onClick={() => setExpanded(!expanded)}>
              {expanded ? copy.showFewer : copy.showAll}
            </button>
          )}

          {/* Reported rather than implied. ChangeFeed.truncated is the server saying the window
              held more than it returned, and a list that stayed silent about it would let a reader
              conclude the changes it omitted never happened. */}
          {feed.truncated && <p className="eg-recent-changes__note">{copy.truncated}</p>}
        </>
      )}
    </section>
  );
}
